<!-- src/lib/components/MpfInputTextarea/MpfInputTextarea.vue -->
<script lang="ts" setup>
  import { computed, ref, onMounted } from 'vue';
  import './MpfInputTextarea.scss';

  import type { MpfInputTextareaProps, MpfTextareaEmits } from './MpfInputTextarea.props';
  import { mpfTextareaDefaults } from './MpfInputTextarea.props';

  import MpfLabel from '../MpfLabel/MpfLabel.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';

  const props = withDefaults(defineProps<MpfInputTextareaProps>(), mpfTextareaDefaults);
  const emit = defineEmits<MpfTextareaEmits>();

  const hasErrors = computed(() => (props.errors?.length ?? 0) > 0);

  const widthClass = computed(() => {
    if (props.width === 'large') return 'mpf-textarea--width-30';
    if (props.width === 'small') return 'mpf-textarea--width-10';
    if (typeof props.width === 'number') return `mpf-textarea--width-${props.width}`;
    return 'mpf-textarea--width-20';
  });

  const textareaClasses = computed(() => [
    'mpf-textarea',
    widthClass.value,
    hasErrors.value ? 'mpf-textarea--error' : '',
  ]);

  const groupClasses = computed(() => [
    'mpf-form-group',
    hasErrors.value ? 'mpf-form-group--error' : '',
  ]);

  const hintId = computed(() => (props.hint ? `${props.id}-hint` : ''));
  const errorId = computed(() => (hasErrors.value ? `${props.id}-error` : ''));

  const describedBy = computed(() => {
    return [props.ariaDescribedby, hintId.value, errorId.value].filter(Boolean).join(' ').trim();
  });

  function onInput(e: Event) {
    const val = (e.target as HTMLTextAreaElement).value;
    emit('update:modelValue', val);
    emit('input', e);
    updateCounter((e.target as HTMLTextAreaElement).value);
  }

  function onChange(e: Event) {
    emit('change', e);
    updateCounter((e.target as HTMLTextAreaElement).value);
  }

  function onBlur(e: Event) {
    emit('blur', e);
    updateCounter((e.target as HTMLTextAreaElement).value);
  }

  function onFocus(e: Event) {
    emit('focus', e);
  }

  function updateCounter(value) {
    if (props.counter) {
      const str = `${value ?? ""}`;
      const len = str.length || 0;
      const max = +props.maxLength!;

      counterText.value = `${len}${max ? ` out of ${max}` : ""} characters used`;
    }
  }

  const counterText = ref(null);
  onMounted(() => {
    updateCounter(props.modelValue ?? '');
  });
</script>

<template>
  <div :class="groupClasses">
    <MpfLabel
      :for-id="id"
      :label="label"
      :required="required"
      :size="size"
      :aria-describedby="hintId"
      :heading-level-size="headingLevelSize"
    />
    <MpfHint v-if="hint" :hint="hint" :sr-hint="srHint" :id="hintId" />

    <div v-if="hasErrors" :id="errorId" class="mpf-error-message" role="alert" aria-live="polite">
      {{ errors![0] }}
    </div>

    <div class="mpf-textarea__wrapper">
      <textarea
        :id="id"
        :name="name || id"
        :class="textareaClasses"
        :aria-describedby="describedBy || undefined"
        :required="required || undefined"
        v-bind="$attrs"
        :value="modelValue"
        @input="onInput"
        @change="onChange"
        @focus="onFocus"
        @blur="onBlur"
        :rows="rows"
        :maxlength="maxLength || undefined"
        data-private
      />

      <div
        v-if="props.counter"
        :class="{ 'mpf-character-count__message--error': (modelValue as unknown as string)?.length > (maxLength as number) }"
        aria-hidden="true"
      >
        {{ counterText }}
      </div>
    </div>
  </div>
</template>
